import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { BorrowerApi } from '../../../../../generated/internalApi/api/BorrowerApi';
import { forkJoin, Observable, throwError } from 'rxjs';
import {
  LDCommonWebApiResponseApiResponse,
  LDCLPBPPublicApiModelsCommonDeviceInfo,
  LDCLPBPPublicApiModelsBorrowersRegisterForPersonalLoanRequest,
  LDCLPBPPublicApiModelsBorrowersRegisterForPersonalLoanResponse,
  LDCLPBPPublicApiModelsBorrowersEmailAvailabilityResponse,
  LDCLPBPPublicApiModelsBorrowersRegisterForMortgageRequest as RegisterBorrowerForMortgageRequest,
  LDCLPBPPublicApiModelsBorrowersRegisterForMortgageResponse as RegisterBorrowerForMortgageResponse
} from '../../../../../generated/internalApi/model/models';

import LoanPurposeEnum = LDCLPBPPublicApiModelsBorrowersRegisterForPersonalLoanRequest.LoanPurposeEnum;
import CreditScoreEstimateEnum = LDCLPBPPublicApiModelsBorrowersRegisterForPersonalLoanRequest.CreditScoreEstimateEnum;

import {
  Borrower,
  BorrowerAssociationData,
  GetBorrowerEmailAvailabilityResponse,
  GetBorrowerResponse,
  GetMortgageBorrower,
  MortgageBorrower,
  MortgageBorrowerByWebGuidResponse,
  PersonalBorrower,
  PersonalLoanRegistrationRequest,
  PersonalLoanRegistrationResponse,
  RegisterMortgageBorrowerRequest,
  RegisterMortgageBorrowerResponse
} from '../../models/index';

import {
  Device,
  CommonAddress,
  EmploymentInfo,
  LoanApplicationsStates,
  UpdateBorrowerRequest
} from '../../models/index';

import OccupancyStatusEnum = UpdateBorrowerRequest.OccupancyStatusEnum;
import { SessionService } from '../session/shared/session/session-service/session.service';
import { SETTINGS } from '../../../clpbp-app.settings';
import { Utils } from '../../fork-common-controls/utils.service';
import { ApiService } from '../api.service';

@Injectable()
export class BorrowerService extends ApiService {

  private get borrowerGuid(): string {
    return this.sessionService.borrowerGuid;
  }

  private get personalBorrowerGuid(): string {
    return this.sessionService.personalLoanSession.data.borrowerData.borrowerGuid;
  }

  private get primaryBorrowerId(): string {
    return this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData.borrowerId;
  }

  private get borrowerCache(): GetBorrowerResponse {
    return this.sessionService.personalLoanSession.data.borrowerCache;
  }

  private set borrowerCache(borrowerCache: GetBorrowerResponse) {
    this.sessionService.personalLoanSession.data.borrowerCache = borrowerCache;
  }

  constructor(
    private borrowerApi: BorrowerApi,
    private http: Http,
    protected sessionService: SessionService) {
    super(sessionService);
  }

  private getUpdateBorrowerRequestEnumValue(rawValue: string = ''): string {
    return rawValue.replace(/\s/g, '');
  }

  initializeBorrowerSession(loanGuid: string): Observable<LoanApplicationsStates.PersonalStateEnum> {
    return forkJoin([this.getBorrower(), this.getBorrowerLoanApplicationsStates(loanGuid)]).map((data: any) => {
      return data[1].personalState;
    }, error => throwError(error));
  }

  public getUnifiedBorrower(): Observable<Borrower> {
    return this.getBorrowerFromApi()
      .map(borrowerResponse => {
        return new PersonalBorrower(borrowerResponse);
      });
  }

  public getEmploymentStatusEnum(employmentStatus: string): UpdateBorrowerRequest.EmploymentStatusEnum {
    const enumValue: string = this.getUpdateBorrowerRequestEnumValue(employmentStatus);
    return UpdateBorrowerRequest.EmploymentStatusEnum[enumValue];
  }

  private getBorrowerLoanApplicationsStates(loanGuid: string): Observable<LoanApplicationsStates> {
    return this.borrowerApi.borrowerGetBorrowerLoanApplicationsStates(
      this.borrowerGuid,
      loanGuid,
      this.authToken
    )
    .map(response => response.data)
    .catch(err => throwError(err));
  }

  public getMortgageBorrower(): Observable<GetMortgageBorrower> {
    return this.borrowerApi.borrowerBorrowerGetMortgageBorrower(this.primaryBorrowerId, this.authToken)
      .map(response => response['data']);
  }

  public getBorrowerPersonalStateEnum(loanGuid?: string): Observable<LoanApplicationsStates.PersonalStateEnum> {
    loanGuid = loanGuid || this.sessionService.personalLoanSession.data.loanApplicationGuid;
    return this.getBorrowerLoanApplicationsStates(loanGuid)
      .map(data => data.personalState);
  }

  public updateBorrower(request: UpdateBorrowerRequest): Observable<LDCommonWebApiResponseApiResponse> {
    return this.borrowerApi.borrowerUpdateBorrower(
      this.sessionService.borrowerGuid,
      request,
      this.sessionService.data.authData.token
    )
    .catch(err => throwError(err));
  }

  public updateBorrowerEmployment(
    employerName?: string,
    employmentPosition?: string,
    yearsWithEmployer?: number,
    employmentStartDate?: Date,
    workAddress?: string,
    workAddressUnit?: string,
    workCity?: string,
    workState?: string,
    workZip?: string,
    workPhone?: string,
    workExtension?: string,
    employmentStatus?: string): Observable<LDCommonWebApiResponseApiResponse> {

      // Because we're making use of the PATCH endpoint, sending empty fields will overwrite those values.
      // So we're assuming that whatever they put in the form, they intend to be sending.
      // From the user's point of view, she doesn't know that leaving fields blank would preserve their exisitng value.
      // It is the same as setting them to empty.
      // If you really intend to patch only part of the borrower's employment information,
      // create a partial LDCLPBPPublicApiModelsBorrowersUpdateBorrowerRequest (UpdateBorrowerRequest) object and call
      // the updateBorrower function directly with that.
      const address: CommonAddress = {
        addressLine1: workAddress,
        addressLine2: workAddressUnit,
        city: workCity,
        state: workState,
        zipCode: workZip
      };

      const employmentInfo: EmploymentInfo = {
        name: employerName,
        position: employmentPosition,
        startDate: employmentStartDate,
        lengthYears: yearsWithEmployer,
        phoneNumber: workPhone,
        extension: workExtension,
        address
      };

      const request: UpdateBorrowerRequest = {
        employmentStatus: this.getEmploymentStatusEnum(employmentStatus),
        currentEmployer: employmentInfo
      };

      // Making use of PATCH endpoint through this function.
      return this.updateBorrower(request).map(response => {
        return response;
      }).catch((err, caught) => {
        console.error(err);
        return throwError(err);
      });
  }

  public borrowerEmailAvailability(emailAddress: string): Observable<GetBorrowerEmailAvailabilityResponse> {
    const authorizationString = this.sessionService.data.authData.token;

    // Bypassing generated api client because we are unable to specify our own encoding with the current swagger code generation.
    // Because we can't specify our own encoding, the angular http client doesn't encode symbols like +, so it is ignored backend.
    const url = '/borrowers/emails/availability?emailAddress=' + encodeURIComponent(emailAddress);
    const path = SETTINGS.API.URL + '/' + SETTINGS.API.NAME_SPACE + url;

    return this.http.get(path).map(response => {
      return response.json().data;
    })
    .catch(err => {
      console.error(err);
      return throwError(err);
    });
  }

  public getAssociatedLoans(): Observable<BorrowerAssociationData[]> {
    const token = this.sessionService.data.authData.token;

    return this.borrowerApi.borrowerGetAssociation(
      token
    )
    .map(response => response.data)
    .catch(err => throwError(err));
  }

  public getBorrower(fromApi: boolean = false): Observable<GetBorrowerResponse> {

    /* LDTSULA-3131-borrowerCache returns the old value: In order to keep the singleton of borrowerCache,
    refactored borrowerCache to be stored in PersonalLoanSessionService  */

    if (!this.borrowerCache || fromApi) {
      return this.getBorrowerFromApi();
    }

    return new Observable<GetBorrowerResponse>(observer => {
      observer.next(this.borrowerCache);
      observer.complete();
    });
}

  private getBorrowerFromApi(): Observable<GetBorrowerResponse> {
    const borrowerGuid = this.sessionService.borrowerGuid;

    return this.borrowerApi.borrowerGetBorrower(
      borrowerGuid,
      this.authToken
    )
    .map(response => this.borrowerCache = response.data)
    .catch(err => throwError(err));
  }

  public registerBorrowerForPersonalLoanByModel(
    request: PersonalLoanRegistrationRequest
  ): Observable<PersonalLoanRegistrationResponse> {

    const authorizationString = this.sessionService.data.authData.token;
    return this.borrowerApi.borrowerRegisterForPersonalLoan(request, authorizationString).map((response) => {
      return response.data;
    }).catch((err, caught) => {
      console.error(err);

      return throwError(err);
    });
  }

  public registerBorrowerForPersonalLoan(
    loanPurpose?: string,
    requestedLoanAmount?: number,
    emailAddress?: string,
    password?: string,
    firstName?: string,
    lastName?: string,
    nameSuffix?: string,
    dateOfBirth?: Date,
    phoneNumber?: string,
    address?: CommonAddress,
    occupancyStatus?: string,
    homeMonthlyPayment?: number,
    annualIncome?: number,
    borrowerSource?: string,
    creditScoreEstimate?: string,
    device?: Device,
    termsOfUseAccepted?: boolean,
    eSignConsentAccepted?: boolean,
    tcpaDisclosureAccepted?: boolean,
    creditReportAuthorizationAccepted?: boolean,
    jornayaLeadID?: string,
    interactionGuid?: string
  ): Observable<PersonalLoanRegistrationResponse> {

    const reqLoanPurpose = LoanPurposeEnum[loanPurpose];
    const reqOccupancyStatus = OccupancyStatusEnum[occupancyStatus];
    const reqCreditScoreEstimate = CreditScoreEstimateEnum[creditScoreEstimate];

    const reqDevice: LDCLPBPPublicApiModelsCommonDeviceInfo = {
      latitude: device.latitude,
      longitude: device.longitude,
      blackBoxId: device.blackBoxId
    };

    const request = {
      loanPurpose: reqLoanPurpose,
      requestedLoanAmount,
      emailAddress,
      password,
      firstName,
      nameSuffix,
      lastName,
      address,
      phoneNumber,
      dateOfBirth,
      occupancyStatus: reqOccupancyStatus,
      jornayaLeadID,
      homeMonthlyPayment,
      annualIncome,
      creditScoreEstimate: reqCreditScoreEstimate,
      borrowerSource,
      isPhoneContactConsentAccepted: true,
      termsOfUseAccepted,
      eSignConsentAccepted,
      tcpaDisclosureAccepted,
      creditReportAuthorizationAccepted,
      deviceInfo: reqDevice,
      interactionGuid
    } as PersonalLoanRegistrationRequest;

    const authorizationString = this.sessionService.data.authData.token;
    return this.borrowerApi.borrowerRegisterForPersonalLoan(request, authorizationString).map((response) => {

      return response.data;
    }).catch((err, caught) => {
      console.error(err);

      return throwError(err);
    });
  }

  /**
   * As applicable (first time ) registers the user and creates a personal loan form them as well
   * @param request Object representing the payload for the API call
   */
  public registerBorrowerPersonalLoan(request: PersonalLoanRegistrationRequest ): Observable<PersonalLoanRegistrationResponse> {

    const authorizationString = this.sessionService.data.authData.token;

    return this.borrowerApi.borrowerRegisterForPersonalLoan(request, authorizationString)
    .map((response) => {
      return response.data;
    })
    .catch((err, caught) => {
      console.error(err);
      return throwError(err);
    });
  }

  // TODO: The Swagger models generated by the API don't reflect what the API is actually returning, this needs to be fixed.
  // The API is returning data: { MortgageBorrowerByWebGuidResponse } instead of just MortgageBorrowerByWebGuidResponse,
  // so we'll force a hard cast of the response.
  public getBorrowerByWebGuid(borrowerWebGuid: string): Observable<MortgageBorrowerByWebGuidResponse> {
    return this.borrowerApi
      .borrowerBorrowerGetMortgageBorrowerByWebGuid(borrowerWebGuid, this.authToken)
      .map(response => (<any>response).data);
  }
}
